import bowser from 'bowser';
import type { RefObject } from 'react';
import gsap from 'gsap';
import { useCallback, useEffect, useState } from 'react';
import useWindowSize from '../../../../hooks/useWindowSize';

export const originalSize = {
  w: 1026.74,
  h: 518.73,
  y0: 213.24,
  safeArea: {
    h: 344,
    w: 590,
  },
};

export default function useBackgroundFan(
  shapePathRef: RefObject<SVGPathElement | null>,
  shapeContainerRef: RefObject<HTMLDivElement | null>,
  maskedElementsRef: RefObject<HTMLDivElement | null>,
  clipId: string,
): {
  goFullScreen: (fullscreen: boolean) => void;
  isFullScreen: boolean;
  getSize: (isFullScreen: boolean) => {
    x: number;
    y: number;
    scale: number;
  };
} {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const { width, height } = useWindowSize();

  const getSize = useCallback(
    (isFullScreen: boolean = false) => {
      const shapeClientBounds = shapeContainerRef?.current?.getBoundingClientRect();
      const containerSize = {
        width: maskedElementsRef?.current?.offsetWidth || 0,
        height: maskedElementsRef?.current?.offsetHeight || 0,
      };
      const windowAspectRatio = containerSize.width / containerSize.height;
      const safeAreaAspectRatio = originalSize.safeArea.w / originalSize.safeArea.h;
      let scale = 0;
      let x = 0;
      let y = 0;

      if (shapeClientBounds) {
        scale = shapeClientBounds.width / originalSize.w;

        if (isFullScreen) {
          scale =
            (containerSize.height / originalSize.h) * (originalSize.h / originalSize.safeArea.h);

          if (windowAspectRatio > safeAreaAspectRatio) {
            scale =
              (containerSize.width / originalSize.w) * (originalSize.w / originalSize.safeArea.w);
          }
        }

        x = (containerSize.width - scale * originalSize.w) / 2;
        y = (containerSize.height - scale * originalSize.h) / 2;
      }

      return {
        x,
        y,
        scale,
      };
    },
    [shapeContainerRef, maskedElementsRef], // width, height,
  );

  const setSize = useCallback(
    (x: number, y: number, scale: number) => {
      gsap.set(shapePathRef.current, {
        attr: { transform: `translate(${x}, ${y}), scale(${scale})` },
      });
    },
    [shapePathRef],
  );

  const goFullScreen = useCallback(
    (fullscreen: boolean = true) => {
      const toSize = getSize(fullscreen);
      gsap.to(shapePathRef.current, {
        duration: 1,
        ease: 'Expo.easeOut',
        attr: {
          transform: `translate(${toSize.x}, ${toSize.y}), scale(${toSize.scale})`,
        },
        onUpdate: () => {
          // Force a re-render on Safari (clipPath in safari can be buggy and not re-rendering, only after a repaint).
          if (maskedElementsRef.current && bowser.safari) {
            gsap.set(maskedElementsRef.current, { clipPath: 'none' });
            gsap.set(maskedElementsRef.current, { clipPath: `url(#${clipId})` });
          }
        },
        onComplete: () => {
          setIsFullScreen(fullscreen);
        },
      });
    },
    [getSize, shapePathRef, maskedElementsRef, clipId],
  );

  useEffect(() => {
    const { x, y, scale } = getSize(isFullScreen);
    setSize(x, y, scale);
  }, [height, width, isFullScreen, getSize, setSize]);

  return {
    goFullScreen,
    isFullScreen,
    getSize,
  };
}
